import { Fragment, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { FileSpreadsheet, Search, Loader2, ChevronDown, ChevronRight, Receipt } from 'lucide-react';
import { formatCurrency, getLocalDateString } from '../utils/formatters';

const toDR = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const firstOfMonth = () => { const d = getLocalDateString(); return `${d.slice(0, 8)}01`; };

const Stat = ({ label, value, testId, accent }) => (
  <div className="rounded-lg border bg-white p-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-xl font-bold mt-1 ${accent || 'text-slate-800'}`}>{value}</p>
  </div>
);

const ecfCls = (estado) => {
  if (!estado) return '';
  if (/acept|cola/i.test(estado)) return 'bg-emerald-100 text-emerald-700';
  if (/rechaz/i.test(estado)) return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-800';
};

const Detail = ({ r }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs p-3 bg-slate-50" data-testid={`r607-detail-${r.ncf || r.documento}`}>
    <div><span className="text-slate-500">Documento:</span> {r.origen} {r.documento}</div>
    <div><span className="text-slate-500">Tipo ID:</span> {r.tipo_id === '1' ? '1 - RNC' : r.tipo_id === '2' ? '2 - Cédula' : '-'}</div>
    <div><span className="text-slate-500">NCF modificado:</span> {r.ncf_modificado || '-'}</div>
    <div><span className="text-slate-500">Tipo ingreso:</span> {r.tipo_ingreso}</div>
    <div><span className="text-slate-500">Efectivo:</span> {formatCurrency(r.efectivo)}</div>
    <div><span className="text-slate-500">Cheque/Transf.:</span> {formatCurrency(r.cheque_transferencia)}</div>
    <div><span className="text-slate-500">Tarjeta:</span> {formatCurrency(r.tarjeta)}</div>
    <div><span className="text-slate-500">Venta a crédito:</span> {formatCurrency(r.credito)}</div>
    <div><span className="text-slate-500">Otras formas:</span> {formatCurrency(r.otras)}</div>
    {r.ncf.startsWith('E') && (
      <>
        <div><span className="text-slate-500">Estado e-CF:</span> {r.ecf_estado || '-'}</div>
        <div><span className="text-slate-500">Cód. seguridad:</span> <span className="font-mono">{r.ecf_codigo_seguridad || '-'}</span></div>
        <div className="col-span-2 md:col-span-1"><span className="text-slate-500">TrackId:</span> <span className="font-mono break-all">{r.ecf_track_id || '-'}</span></div>
      </>
    )}
  </div>
);

export default function Report607() {
  const [desde, setDesde] = useState(firstOfMonth());
  const [hasta, setHasta] = useState(getLocalDateString());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tipo, setTipo] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState({});

  const params = () => ({ fecha_inicio: toDR(desde), fecha_fin: toDR(hasta) });

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/reports/607`, { params: params() });
      setData(resp.data);
      setOpen({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando reporte');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/reports/607/excel`, { params: params(), responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `607_${desde}_${hasta}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Error descargando Excel');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.registros || []).filter(r =>
      (!tipo || r.tipo_ncf === tipo) &&
      (!term || [r.ncf, r.cliente, r.rnc_cedula, r.documento].some(v => String(v || '').toLowerCase().includes(term)))
    );
  }, [data, tipo, q]);

  const t = data?.totales;

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="report-607-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2"><Receipt className="text-blue-600" /> Reporte de Ventas 607</h1>
          <p className="text-sm text-slate-500 mt-1">Todas las ventas del período: con NCF, e-CF o sin comprobante (sin anuladas).</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Desde</Label><Input type="date" value={desde} onChange={e => setDesde(e.target.value)} data-testid="r607-desde" /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" value={hasta} onChange={e => setHasta(e.target.value)} data-testid="r607-hasta" /></div>
          <Button onClick={fetchData} disabled={loading} data-testid="r607-generar-btn">
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search className="mr-2" size={16} />} Generar
          </Button>
          <Button onClick={downloadExcel} disabled={downloading || !data} className="bg-emerald-600 hover:bg-emerald-700" data-testid="r607-excel-btn">
            {downloading ? <Loader2 className="animate-spin mr-2" size={16} /> : <FileSpreadsheet className="mr-2" size={16} />} Descargar 607 (Excel)
          </Button>
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Comprobantes" value={data.cantidad} testId="r607-cantidad" />
          <Stat label="Monto Facturado" value={formatCurrency(t.monto_facturado)} testId="r607-monto" />
          <Stat label="ITBIS Facturado" value={formatCurrency(t.itbis)} testId="r607-itbis" />
          <Stat label="Total" value={formatCurrency(t.total)} testId="r607-total" accent="text-blue-700" />
        </div>
      )}

      {data?.resumen_por_tipo?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="r607-resumen">
          <button onClick={() => setTipo('')} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${!tipo ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-slate-50'}`} data-testid="r607-tipo-todos">Todos</button>
          {data.resumen_por_tipo.map(g => (
            <button key={g.tipo_ncf} onClick={() => setTipo(g.tipo_ncf)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${tipo === g.tipo_ncf ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-slate-50'}`} data-testid={`r607-tipo-${g.tipo_ncf}`}>
              {g.tipo_ncf} · {g.cantidad} · {formatCurrency(g.total)}
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="p-3 border-b">
            <Input placeholder="Buscar por NCF, cliente, RNC o documento…" value={q} onChange={e => setQ(e.target.value)} className="max-w-md" data-testid="r607-search" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="r607-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="w-8"></th>
                  <th className="text-left p-2">Fecha</th>
                  <th className="text-left p-2">NCF / e-CF</th>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-left p-2">RNC/Cédula</th>
                  <th className="text-right p-2">Monto</th>
                  <th className="text-right p-2">ITBIS</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-left p-2">e-CF</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-10"><Loader2 className="animate-spin inline text-blue-500" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-10 text-slate-500" data-testid="r607-empty">No hay comprobantes en el período.</td></tr>
                ) : rows.map(r => (
                  <Fragment key={`${r.origen}-${r.id}`}>
                    <tr className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(o => ({ ...o, [r.id]: !o[r.id] }))} data-testid={`r607-row-${r.ncf || r.documento}`}>
                      <td className="pl-2 text-slate-400">{open[r.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                      <td className="p-2 whitespace-nowrap">{r.fecha}</td>
                      <td className="p-2 font-mono text-xs">{r.ncf || <span className="font-sans text-[11px] text-slate-400 italic">Sin comprobante</span>}</td>
                      <td className="p-2">{r.tipo_ncf}{r.origen !== 'Factura' && <span className="ml-1 text-[10px] text-purple-600">({r.origen})</span>}</td>
                      <td className="p-2">{r.cliente}</td>
                      <td className="p-2 font-mono text-xs">{r.rnc_cedula || '-'}</td>
                      <td className="p-2 text-right">{formatCurrency(r.monto_facturado)}</td>
                      <td className="p-2 text-right">{formatCurrency(r.itbis)}</td>
                      <td className="p-2 text-right font-semibold">{formatCurrency(r.total)}</td>
                      <td className="p-2">{r.ecf_estado && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ecfCls(r.ecf_estado)}`}>{r.ecf_estado}</span>}</td>
                    </tr>
                    {open[r.id] && <tr><td colSpan={10}><Detail r={r} /></td></tr>}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
